import React, { useState } from "react";
import dayjs from "dayjs";
import { useIntl } from "react-intl";
import DisplayFields from "../DisplayFields";
import DeleteModal from "../../DeleteModal/";
import {
    HiOutlineBuildingStorefront,
    HiBars3BottomLeft,
    HiOutlineUserGroup,
    HiOutlineMapPin,
} from "react-icons/hi2";

import { formatMeetingDate } from "../../../../utils/Meetings/functions";

const iconSize = 20;
const iconStroke = 1.6;

const MeetingDetailsModal = ({
    eventData,
    attendees,
    user_id,
    locale,
    meetingTypes,
    handleDelete,
    isUserMeetingOrganizer,
}) => {
    const intl = useIntl();

    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);

    const isDeleteOpen = () => {
        if (showDeleteModal) {
            setShowDeleteModal(false);
        } else {
            setShowDeleteModal(true);
        }
    };

    return (
        <div className="h-full flex flex-col justify-between px-3 md:pl-10 md:pr-10 gap-3 relative">
            <div className="w-full overflow-y-auto no-scrollbar pb-5">
                <h2 className="flex flex-col justify-left w-full ml-10">
                    <div
                        className=" text-xl text-black text-left break-words whitespace-normal max-w-full overflow-hidden"
                        style={{
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 2,
                        }}
                    >
                        {eventData.title
                            ? eventData.title
                            : intl.formatMessage({
                                  id: "null",
                              })}
                    </div>{" "}
                    <div>
                        {" "}
                        <div className="flex items-center mb-2 capitalize pt-1">
                            {formatMeetingDate(eventData.start_date, locale)} -{" "}
                            {dayjs(eventData.start_date).isSame(
                                dayjs(eventData.end_date),
                                "day"
                            )
                                ? dayjs(eventData.end_date)
                                      .locale(locale)
                                      .format("HH:mm")
                                : formatMeetingDate(eventData.end_date, locale)}
                        </div>
                    </div>
                </h2>
                <div className="flex flex-col gap-">
                    <DisplayFields
                        center={true}
                        isForm={false}
                        icon={
                            <div
                                className={`h-4 w-4 rounded-full`}
                                style={{
                                    height: "14px",
                                    width: "14px",
                                    backgroundColor: meetingTypes?.find(
                                        (type) =>
                                            type.meeting_type_id ===
                                            eventData.meeting_type_id
                                    )?.color,
                                }}
                            />
                        }
                    >
                        <div className="w-full text-left">
                            {meetingTypes
                                .filter(
                                    (type) =>
                                        type.meeting_type_id ===
                                        eventData.meeting_type_id
                                )
                                .map((type) => (
                                    <div key={type.meeting_type_id}>
                                        {type.description}
                                    </div>
                                ))}
                        </div>
                    </DisplayFields>
                    <DisplayFields
                        center={true}
                        isForm={false}
                        icon={
                            <HiOutlineBuildingStorefront
                                size={iconSize}
                                strokeWidth={iconStroke}
                            />
                        }
                    >
                        <div className="text-left">
                            {eventData.client &&
                            eventData.client.client_id != null ? (
                                <>
                                    {eventData.client.client_name}
                                    <span className="text-gray-600 ml-2">
                                        (ID: {eventData.client.client_id})
                                    </span>
                                </>
                            ) : (
                                intl.formatMessage({
                                    id: "meetingModal.noClient",
                                })
                            )}
                        </div>
                    </DisplayFields>
                    {eventData.address_1 && (
                        <DisplayFields
                            icon={
                                <HiOutlineMapPin
                                    size={iconSize}
                                    strokeWidth={iconStroke}
                                />
                            }
                            center={true}
                            isForm={false}
                        >
                            <div>
                                {eventData.address_1} {eventData.zip_code}{" "}
                                {eventData.city}
                            </div>
                            <div> {eventData.country}</div>
                        </DisplayFields>
                    )}
                    {eventData.description &&
                        eventData.description.trim() !== "" && (
                            <DisplayFields
                                center={false}
                                isForm={false}
                                icon={<HiBars3BottomLeft size={iconSize} />}
                            >
                                {eventData.description}
                            </DisplayFields>
                        )}
                    <div className="h-2"></div>
                    {((isUserMeetingOrganizer &&
                        eventData.attendees.length > 1) ||
                        !isUserMeetingOrganizer) && (
                        <DisplayFields
                            center={true}
                            isForm={false}
                            icon={
                                <HiOutlineUserGroup
                                    size={iconSize}
                                    strokeWidth={iconStroke}
                                />
                            }
                        >
                            <div className="flex flex-col">
                                <div className="font-medium text-md">
                                    {" "}
                                    {isUserMeetingOrganizer
                                        ? eventData.attendees.length - 1
                                        : eventData.attendees.length}{" "}
                                    {intl.formatMessage({
                                        id: isUserMeetingOrganizer
                                            ? eventData.attendees.length > 2
                                                ? "meetingModal.pluralGuests"
                                                : "meetingModal.oneGuest"
                                            : eventData.attendees.length > 1
                                              ? "meetingModal.pluralParticipants"
                                              : "meetingModal.oneParticipant",
                                    })}{" "}
                                </div>
                            </div>
                        </DisplayFields>
                    )}
                    <ul className="w-full text-md text-gray-800 font-medium flex flex-col gap-1 mt- text-sm ml-10">
                        {eventData.attendees
                            .filter((attendeeId) =>
                                !isUserMeetingOrganizer
                                    ? attendeeId === attendeeId
                                    : attendeeId !== user_id
                            ) // Exclude current user_id
                            .map((attendeeId) => {
                                const attendee = attendees.find(
                                    (a) => a.user_id === attendeeId
                                );
                                if (!attendee) return null;
                                return (
                                    <li key={attendeeId} className="flex">
                                        {attendee.first_name}{" "}
                                        {attendee.last_name}
                                    </li>
                                );
                            })}
                    </ul>
                </div>
            </div>
            <div className="h-1/6 bg-white flex items-center justify-center sticky bottom-0 pt-1">
                <button
                    className="md:hidden block text-red-600 mb-3"
                    onClick={isDeleteOpen}
                >
                    {intl.formatMessage({
                        id: "meetingModal.deleteMeeting",
                    })}
                </button>
            </div>
            {showDeleteModal && (
                <DeleteModal
                    locale={locale}
                    handleDelete={handleDelete}
                    setShowDeleteModal={isDeleteOpen}
                />
            )}
        </div>
    );
};

export default MeetingDetailsModal;
